import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiSearchLine, RiFilter3Line, RiEditLine, RiDeleteBin6Line } from 'react-icons/ri';
import axios from 'axios';
import './MedicineManagement.css';
import MedicineModal from './MedicineModal';

const MedicineManagement = () => {
    const [medicines, setMedicines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchMedicines();
    }, []);

    const fetchMedicines = async () => {
        try {
            const response = await axios.get('https://api.swasthmitra.com/api/medicine');
            // Add timestamp to image URLs to force refresh
            const sortedMedicines = response.data.map(medicine => ({
                ...medicine,
                imageUrl: medicine.imageUrl ? `${medicine.imageUrl}?t=${Date.now()}` : null,
                imageUrls: medicine.imageUrls ? medicine.imageUrls.map(url => `${url}?t=${Date.now()}`) : []
            })).sort((a, b) =>
                (new Date(b.createdAt) || b._id) - (new Date(a.createdAt) || a._id)
            );
            setMedicines(sortedMedicines);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching medicines:', error);
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this medicine?')) {
            try {
                await axios.delete(`https://api.swasthmitra.com/api/medicine/${id}`);
                fetchMedicines();
            } catch (error) {
                console.error('Error deleting medicine:', error);
            }
        }
    };

    const filteredMedicines = medicines
        .filter(medicine => {
            const matchesSearch = medicine.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = filterCategory === 'all' || medicine.category === filterCategory;
            return matchesSearch && matchesFilter;
        });

    const categories = [...new Set(medicines.map(medicine => medicine.category))];

    return (
        <div className="medicine-management">
            <div className="header">
                <h2>Medicine Management</h2>
                <div className="filters">
                    <div className="search-box">
                        <RiSearchLine />
                        <input
                            type="text"
                            placeholder="Search medicines..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="category-filter">
                        <RiFilter3Line />
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading medicines...</div>
            ) : (
                <div className="medicines-grid">
                    {filteredMedicines.map((medicine) => (
                        <motion.div
                            key={medicine._id}
                            className="medicine-card"
                            whileHover={{ scale: 1.02 }}
                        >
                            <div className="medicine-image">
                                <img
                                    src={medicine.imageUrl || medicine.imageUrls?.[0] || 'default-medicine.jpg'}
                                    alt={medicine.name}
                                    key={medicine.imageUrl || medicine.imageUrls?.[0]}
                                />
                            </div>
                            <div className="medicine-info">
                                <h3>{medicine.name}</h3>
                                <p className="category">{medicine.category}</p>
                                <p className="price">₹{medicine.salePrice}</p>
                                <p className="stock">Stock: {medicine.stockQuantity}</p>
                            </div>
                            <div className="card-actions1">
                                <button
                                    className="edit-btn1"
                                    onClick={() => {
                                        setSelectedMedicine(medicine);
                                        setIsEditing(true);
                                    }}
                                >
                                    <RiEditLine /> Edit
                                </button>
                                <button
                                    className="delete-btn1"
                                    onClick={() => handleDelete(medicine._id)}
                                >
                                    <RiDeleteBin6Line /> Delete
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {selectedMedicine && (
                    <MedicineModal
                        medicine={selectedMedicine}
                        isEditing={isEditing}
                        onClose={() => {
                            setSelectedMedicine(null);
                            setIsEditing(false);
                        }}
                        onSave={fetchMedicines}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default MedicineManagement;
